import { useCallback, useEffect, useRef, useState } from 'react';
import liveScreenSocket, {
  LiveScreenFramePayload,
  LiveScreenStatePayload,
  LiveStreamMode,
} from '../services/liveScreenSocket';

const DEFAULT_MAX_FRAME_BYTES = 350_000;
const HEARTBEAT_MS = 30_000;

function estimateBase64Bytes(dataBase64: string): number {
  const len = dataBase64.length;
  const padding = dataBase64.endsWith('==') ? 2 : dataBase64.endsWith('=') ? 1 : 0;
  return Math.floor((len * 3) / 4) - padding;
}

function base64ToObjectUrl(dataBase64: string, mimeType = 'image/jpeg'): string {
  const binary = atob(dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: mimeType });
  return URL.createObjectURL(blob);
}

export interface UseLiveScreenSubscriptionOptions {
  deviceId: string | null | undefined;
  mode: LiveStreamMode;
  enabled?: boolean;
  /** When true, pause (unsubscribe) while the observed element is off-screen. */
  pauseWhenNotVisible?: boolean;
  rootMargin?: string;
  maxFrameBytes?: number;
  observeTarget?: HTMLElement | null;
}

export interface UseLiveScreenSubscriptionResult {
  frameUrl: string | null;
  state: LiveScreenStatePayload | null;
  lastFrameAt: Date | null;
  connecting: boolean;
  error: string | null;
  visible: boolean;
}

export function useLiveScreenSubscription(
  options: UseLiveScreenSubscriptionOptions,
): UseLiveScreenSubscriptionResult {
  const {
    deviceId,
    mode,
    enabled = true,
    pauseWhenNotVisible = false,
    rootMargin = '80px',
    maxFrameBytes = DEFAULT_MAX_FRAME_BYTES,
    observeTarget = null,
  } = options;

  const [frameUrl, setFrameUrl] = useState<string | null>(null);
  const [state, setState] = useState<LiveScreenStatePayload | null>(null);
  const [lastFrameAt, setLastFrameAt] = useState<Date | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState(!pauseWhenNotVisible);

  const frameUrlRef = useRef<string | null>(null);
  const lastSequenceRef = useRef(-1);
  const subscribedRef = useRef(false);
  const deviceIdRef = useRef(deviceId);

  const revokeFrame = useCallback(() => {
    if (frameUrlRef.current) {
      URL.revokeObjectURL(frameUrlRef.current);
      frameUrlRef.current = null;
    }
    setFrameUrl(null);
  }, []);

  // Visibility observer
  useEffect(() => {
    if (!pauseWhenNotVisible) {
      setVisible(true);
      return undefined;
    }
    if (!observeTarget) {
      setVisible(false);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setVisible(Boolean(entry?.isIntersecting));
      },
      { root: null, rootMargin, threshold: 0.05 },
    );
    observer.observe(observeTarget);
    return () => observer.disconnect();
  }, [pauseWhenNotVisible, observeTarget, rootMargin]);

  // Frame / state listeners (shared socket)
  useEffect(() => {
    if (!enabled || !deviceId) return undefined;

    const offFrame = liveScreenSocket.onFrame((frame: LiveScreenFramePayload) => {
      if (frame.deviceId && frame.deviceId !== deviceId) return;
      const dataBase64 = frame.dataBase64;
      if (!dataBase64) return;

      const seq = Number(frame.sequence ?? 0);
      if (seq > 0 && seq <= lastSequenceRef.current) return;
      if (estimateBase64Bytes(dataBase64) > maxFrameBytes) return;

      lastSequenceRef.current = seq;
      try {
        const nextUrl = base64ToObjectUrl(dataBase64, frame.mimeType || 'image/jpeg');
        if (frameUrlRef.current) URL.revokeObjectURL(frameUrlRef.current);
        frameUrlRef.current = nextUrl;
        setFrameUrl(nextUrl);
        setLastFrameAt(frame.capturedAt ? new Date(frame.capturedAt) : new Date());
        setError(null);
      } catch {
        // ignore corrupt frames
      }
    });

    const offState = liveScreenSocket.onState((next: LiveScreenStatePayload) => {
      if (next.deviceId && next.deviceId !== deviceId) return;
      setState(next);
    });

    const offError = liveScreenSocket.onError((err) => {
      setError(err?.message || 'Live Screen error');
    });

    return () => {
      offFrame();
      offState();
      offError();
    };
  }, [enabled, deviceId, maxFrameBytes]);

  // Subscribe / unsubscribe lifecycle
  useEffect(() => {
    deviceIdRef.current = deviceId;
    const shouldSubscribe = Boolean(enabled && deviceId && (!pauseWhenNotVisible || visible));

    let cancelled = false;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

    const run = async () => {
      if (!shouldSubscribe || !deviceId) {
        if (subscribedRef.current && deviceId) {
          subscribedRef.current = false;
          await liveScreenSocket.unsubscribe(deviceId).catch(() => undefined);
        }
        setConnecting(false);
        return;
      }

      setConnecting(true);
      setError(null);
      lastSequenceRef.current = -1;

      try {
        await liveScreenSocket.connect();
        if (cancelled) return;
        await liveScreenSocket.subscribe(deviceId, mode);
        if (cancelled) {
          await liveScreenSocket.unsubscribe(deviceId).catch(() => undefined);
          return;
        }
        subscribedRef.current = true;
        setConnecting(false);

        heartbeatTimer = setInterval(() => {
          liveScreenSocket.heartbeat(deviceId);
        }, HEARTBEAT_MS);
      } catch (err: any) {
        if (!cancelled) {
          setConnecting(false);
          setError(err?.message || 'Failed to subscribe');
          subscribedRef.current = false;
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      const id = deviceIdRef.current;
      if (subscribedRef.current && id) {
        subscribedRef.current = false;
        void liveScreenSocket.unsubscribe(id).catch(() => undefined);
      }
    };
  }, [enabled, deviceId, mode, pauseWhenNotVisible, visible]);

  // Cleanup object URLs on unmount / device change
  useEffect(() => () => {
    revokeFrame();
  }, [deviceId, revokeFrame]);

  return { frameUrl, state, lastFrameAt, connecting, error, visible };
}

export default useLiveScreenSubscription;
